import React from "react";

/**
 * Iter v34aw · Batch 3 — Global bento styling.
 * Bento theme applied at ui-kit level so every page automatically gets:
 *   - rounded-2xl cards with soft border-stone-200/70
 *   - rounded-lg inputs
 *   - rounded-full pill badges
 *   - rounded-lg buttons
 * Terra brand color kept for backwards compatibility on existing primary CTAs.
 */

export function PageHeader({ overline, title, description, action }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 md:gap-4 pb-2">
      <div className="space-y-1 md:space-y-1.5 min-w-0">
        {overline && <p className="text-[10px] uppercase tracking-[0.25em] text-stone-500 font-semibold">{overline}</p>}
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-stone-900 break-words leading-tight">{title}</h1>
        {description && <p className="text-xs sm:text-sm text-stone-500 max-w-2xl">{description}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function Card({ children, className = "", ...props }) {
  return (
    <div className={`bg-white border border-stone-200/70 rounded-2xl ${className}`} {...props}>{children}</div>
  );
}

export function KPI({ label, value, sub, accent = "stone", testid }) {
  const accents = {
    terra: "border-l-terra-500",
    moss: "border-l-emerald-500",
    info: "border-l-blue-500",
    warn: "border-l-amber-500",
    stone: "border-l-stone-400",
    danger: "border-l-red-600",
  };
  return (
    <Card className={`border-l-4 ${accents[accent] || accents.stone} p-5`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-semibold">{label}</p>
      <p className="font-display text-3xl mt-2 text-stone-900 font-mono-num">{value}</p>
      {sub && <p className="text-xs text-stone-500 mt-2">{sub}</p>}
    </Card>
  );
}

export function Button({ variant = "primary", className = "", children, ...props }) {
  const base = "inline-flex items-center justify-center gap-2 text-sm font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-50";
  const variants = {
    primary: "bg-terra-500 text-white hover:bg-terra-600",
    secondary: "bg-white text-stone-800 border border-stone-300 hover:bg-stone-50",
    ghost: "text-stone-700 hover:bg-stone-100",
    danger: "bg-red-600 text-white hover:bg-red-700",
    dark: "bg-stone-900 text-white hover:bg-stone-800",
  };
  return (
    <button className={`${base} ${variants[variant]} ${className}`} {...props}>{children}</button>
  );
}

export function Field({ label, children, hint, required }) {
  return (
    <div>
      {label && (
        <label className="block text-xs font-semibold uppercase tracking-wider text-stone-600 mb-1.5">
          {label}{required && <span className="text-terra-500 ml-0.5">*</span>}
        </label>
      )}
      {children}
      {hint && <p className="text-[11px] text-stone-400 mt-1">{hint}</p>}
    </div>
  );
}

export function Input(props) {
  return (
    <input
      {...props}
      className={`w-full border border-stone-300 bg-white rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors ${props.className || ""}`}
    />
  );
}

export function Select({ children, ...props }) {
  return (
    <select
      {...props}
      className={`w-full border border-stone-300 bg-white rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none ${props.className || ""}`}
    >
      {children}
    </select>
  );
}

export function Textarea(props) {
  return (
    <textarea
      {...props}
      className={`w-full border border-stone-300 bg-white rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none ${props.className || ""}`}
    />
  );
}

export function Badge({ children, tone = "stone", className = "", ...props }) {
  const tones = {
    stone: "bg-stone-100 text-stone-700 border-stone-200",
    terra: "bg-terra-50 text-terra-700 border-terra-100",
    success: "bg-emerald-50 text-emerald-700 border-emerald-100",
    warn: "bg-amber-50 text-amber-800 border-amber-100",
    danger: "bg-red-50 text-red-700 border-red-100",
    info: "bg-blue-50 text-blue-700 border-blue-100",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    red: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <span {...props} className={`inline-flex items-center text-[10px] uppercase tracking-wider font-semibold border px-2.5 py-0.5 rounded-full ${tones[tone] || tones.stone} ${className}`}>
      {children}
    </span>
  );
}

export function Modal({ open, onClose, title, children, size = "md", topmost = false, level = 0 }) {
  if (!open) return null;
  const sizes = { sm: "max-w-md", md: "max-w-2xl", lg: "max-w-4xl", xl: "max-w-6xl" };
  // Iter v34bp — `topmost` prop bumps z-index (z-[60]) agar Modal ini muncul
  // DI ATAS Modal parent (z-50). Dipakai saat File Preview / Foto Viewer dipanggil
  // dari dalam Modal Detail SJ / Portal Klien.
  // Iter v34cq — `level` untuk stack 3 lapis (Detail PO → Daftar SJ → Foto).
  const lvl = level || (topmost ? 1 : 0);
  const z = lvl >= 2 ? "z-[70]" : lvl === 1 ? "z-[60]" : "z-50";
  return (
    <div className={`fixed inset-0 ${z} flex items-end sm:items-center justify-center p-2 sm:p-4 bg-stone-900/40 backdrop-blur-sm overflow-y-auto`} data-testid="modal-backdrop">
      <div className={`bg-white w-full ${sizes[size]} max-h-[95vh] sm:max-h-[90vh] overflow-y-auto border border-stone-200 rounded-2xl shadow-xl`}>
        <div className="flex items-center justify-between px-3 sm:px-5 py-3 sm:py-4 border-b border-stone-200 sticky top-0 bg-white z-10 rounded-t-2xl">
          <h3 className="font-display text-base sm:text-lg font-semibold text-stone-900 truncate pr-3">{title}</h3>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-900 text-2xl leading-none px-2 shrink-0" data-testid="modal-close" aria-label="Tutup">×</button>
        </div>
        <div className="p-3 sm:p-5">{children}</div>
      </div>
    </div>
  );
}

export function EmptyState({ title, hint, action }) {
  return (
    <div className="border border-dashed border-stone-300 bg-white rounded-2xl py-16 text-center">
      <p className="font-display text-base font-semibold text-stone-700">{title}</p>
      {hint && <p className="text-xs text-stone-500 mt-1">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/**
 * Iter v34aw · Bento — Skeleton loading state.
 * Usage:
 *   <Skeleton className="h-6 w-40" />
 *   <SkeletonCard lines={3} />
 *   <SkeletonKPI count={4} />
 */
export function Skeleton({ className = "" }) {
  return <div className={`animate-pulse bg-stone-200/70 rounded-lg ${className}`} />;
}

export function SkeletonCard({ lines = 3, className = "" }) {
  return (
    <div className={`rounded-2xl bg-white border border-stone-200/70 p-5 space-y-3 ${className}`}>
      <Skeleton className="h-3 w-20" />
      <Skeleton className="h-7 w-40" />
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className={`h-2.5 ${i === lines - 1 ? "w-1/2" : "w-full"}`} />
      ))}
    </div>
  );
}

export function SkeletonKPI({ count = 4 }) {
  return (
    <div className={`grid grid-cols-2 md:grid-cols-${Math.min(count, 4)} gap-3`}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-2xl bg-white border border-stone-200/70 p-4 space-y-2">
          <Skeleton className="h-2.5 w-16" />
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-2 w-20" />
        </div>
      ))}
    </div>
  );
}

export function SkeletonHero({ dark = false }) {
  const base = dark ? "bg-stone-800" : "bg-white border border-stone-200/70";
  return (
    <div className={`rounded-3xl ${base} p-8 relative overflow-hidden`}>
      <Skeleton className={`h-3 w-32 ${dark ? "!bg-stone-700" : ""}`} />
      <div className="mt-4">
        <Skeleton className={`h-12 w-64 ${dark ? "!bg-stone-700" : ""}`} />
      </div>
      <div className="mt-6 grid grid-cols-2 gap-6">
        <Skeleton className={`h-4 w-full ${dark ? "!bg-stone-700" : ""}`} />
        <Skeleton className={`h-4 w-full ${dark ? "!bg-stone-700" : ""}`} />
      </div>
    </div>
  );
}

export function SkeletonTable({ rows = 5, cols = 6 }) {
  return (
    <div className="rounded-2xl bg-white border border-stone-200/70 overflow-hidden">
      <div className="bg-stone-50 border-b border-stone-200 px-4 py-3">
        <div className={`grid grid-cols-${cols} gap-3`}>
          {Array.from({ length: cols }).map((_, i) => <Skeleton key={i} className="h-3 w-full" />)}
        </div>
      </div>
      <div className="divide-y divide-stone-100">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className={`px-4 py-4 grid grid-cols-${cols} gap-3`}>
            {Array.from({ length: cols }).map((_, c) => <Skeleton key={c} className="h-3 w-full" />)}
          </div>
        ))}
      </div>
    </div>
  );
}
